/**
 * Streaming audio playback for PCM16 24 kHz data.
 *
 * Receives PCM16 chunks from the voice WebSocket and plays them
 * back with gapless scheduling. Supports immediate stop for
 * interruption handling.
 */

const SAMPLE_RATE = 24_000;

export class AudioPlayback {
  private ctx: AudioContext | null = null;
  private analyserNode: AnalyserNode | null = null;
  private scheduledEnd = 0;
  private sources: AudioBufferSourceNode[] = [];
  private _playing = false;

  constructor() {
    this.init();
  }

  private init(): void {
    this.ctx = new AudioContext({ sampleRate: SAMPLE_RATE });
    this.analyserNode = this.ctx.createAnalyser();
    this.analyserNode.fftSize = 256;
    this.analyserNode.connect(this.ctx.destination);
    this.scheduledEnd = 0;
  }

  /**
   * Enqueue a PCM16 audio chunk for gapless playback.
   * @param pcm16 Raw PCM16 (Int16) audio data at 24 kHz.
   */
  enqueue(pcm16: ArrayBuffer): void {
    if (!this.ctx || !this.analyserNode) return;

    // Resume context if suspended (browser autoplay policy).
    if (this.ctx.state === "suspended") {
      void this.ctx.resume();
    }

    // Convert Int16 → Float32
    const int16 = new Int16Array(pcm16);
    const float32 = new Float32Array(int16.length);
    for (let i = 0; i < int16.length; i++) {
      float32[i] = int16[i] / 32768;
    }

    // Create AudioBuffer
    const buffer = this.ctx.createBuffer(1, float32.length, SAMPLE_RATE);
    buffer.getChannelData(0).set(float32);

    // Create source node → analyser → destination
    const source = this.ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(this.analyserNode!);

    // Schedule for gapless playback
    const now = this.ctx.currentTime;
    const startAt = Math.max(now, this.scheduledEnd);
    source.start(startAt);
    this.scheduledEnd = startAt + buffer.duration;

    // Track for cleanup
    this.sources.push(source);
    source.onended = () => {
      const idx = this.sources.indexOf(source);
      if (idx >= 0) this.sources.splice(idx, 1);
      if (this.sources.length === 0) this._playing = false;
    };
    this._playing = true;
  }

  /** Immediately stop all playback (for interruption handling). */
  stop(): void {
    for (const src of this.sources) {
      try {
        src.stop();
        src.disconnect();
      } catch {
        // Already stopped
      }
    }
    this.sources = [];
    this.scheduledEnd = 0;
    this._playing = false;
  }

  /** Whether audio is currently being played. */
  isPlaying(): boolean {
    return this._playing;
  }

  /** Get the AnalyserNode for output waveform visualisation. */
  getAnalyser(): AnalyserNode | null {
    return this.analyserNode;
  }

  /** Release all audio resources. */
  async destroy(): Promise<void> {
    this.stop();
    this.analyserNode?.disconnect();
    this.analyserNode = null;
    if (this.ctx && this.ctx.state !== "closed") {
      await this.ctx.close();
    }
    this.ctx = null;
  }
}
